// Pure timing rules for renewing the access token early. Kept free of axios,
// localStorage and window so the arithmetic can be tested on its own — the
// previous version of this maths logged the whole office out at the hour mark.

export const MIN_LEAD_MS = 30 * 1000;
const MIN_MARGIN_MS = 60 * 1000;
const MAX_MARGIN_MS = 15 * 60 * 1000;
const UNKNOWN_TTL_FALLBACK_MS = 10 * 60 * 1000;
const ASSUMED_TTL_MS = 60 * 60 * 1000;

// The `exp` claim of a JWT in milliseconds, or 0 when it cannot be read. No
// signature check: this only decides *when* to ask the server for a new token.
export function decodeJwtExpMs(token) {
    if (!token || typeof token !== "string") return 0;
    const parts = token.split(".");
    if (parts.length < 2) return 0;
    try {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
        const json = typeof atob === "function"
            ? atob(padded)
            : Buffer.from(padded, "base64").toString("utf8");
        const payload = JSON.parse(json);
        const exp = Number(payload && payload.exp);
        return Number.isFinite(exp) && exp > 0 ? exp * 1000 : 0;
    } catch (_) {
        return 0;
    }
}

// How long before expiry the renewal should happen: a fifth of the lifetime,
// never under a minute, never over fifteen.
export function renewalMarginMs(ttlMs) {
    const base = ttlMs > 0 ? ttlMs : ASSUMED_TTL_MS;
    return Math.min(Math.max(Math.floor(base * 0.2), MIN_MARGIN_MS), MAX_MARGIN_MS);
}

// Milliseconds until the next proactive renewal.
export function computeRefreshDelayMs({ nowMs, expMs = 0, ttlMs = 0 }) {
    if (expMs > 0) {
        return Math.max(MIN_LEAD_MS, expMs - nowMs - renewalMarginMs(ttlMs));
    }
    if (ttlMs > 0) {
        // No readable expiry: assume the token was just issued. Renew at 80% of
        // the lifetime, and in any case at least a minute before it ends.
        return Math.max(MIN_LEAD_MS, Math.min(Math.floor(ttlMs * 0.8), ttlMs - MIN_MARGIN_MS));
    }
    return UNKNOWN_TTL_FALLBACK_MS;
}

// True when the token is expired or already inside the renewal margin — a tab
// waking from the background uses this to renew straight away instead of
// trusting a timer that may have been throttled.
export function isWithinRenewalWindow({ nowMs, expMs = 0, ttlMs = 0 }) {
    if (!(expMs > 0)) return false;
    return expMs - nowMs <= renewalMarginMs(ttlMs);
}
